import React from 'react';
import Icone from './Icone';
import { useCampo } from './Field';
import { molduraCampo } from './Input';

/**
 * Campo cujo valor é escolhido em MODAL: um botão com a moldura de um campo
 * (promovido ao kit na at-62, frente D/D1).
 *
 * ## Por que virou primitivo
 *
 * Ele existia **duas vezes fora do kit**, escrito de novo em cada formulário:
 * `BotaoSelecao` em `components/carro/FormularioVeiculo.tsx` (8 campos) e
 * `BotaoRegional` em `components/associado/FormularioAssociado.tsx` (1 campo,
 * com o texto de vazio fixo). A tela de cotação tem **6 campos** assim e seria a
 * terceira cópia — o consumidor plural que o `13-ui-kit.md` exige para um
 * primitivo nascer chegou, e as três passaram a usar este.
 *
 * Antes disso, nas telas legadas, cada um desses campos eram **duas coisas
 * soltas**: um botão "Selecionar X" e, abaixo, uma linha de texto com o nome
 * escolhido (ou "Nenhum X selecionado") — o que fazia metade do formulário não
 * parecer um formulário.
 *
 * ## 🔴 O contrato de acessibilidade, que é a razão de ser um componente
 *
 * ⚠️ **É componente próprio para poder chamar `useCampo()`.** Como filho direto
 * do `Field`, o JSX do formulário não teria acesso ao contexto — hook não se
 * chama no render do pai.
 *
 * ⚠️ **`aria-labelledby` cita o rótulo E o próprio botão**, nesta ordem: o leitor
 * de tela concatena e anuncia "Cor, VERMELHO". Citar só o rótulo **substituiria**
 * o conteúdo e o valor escolhido deixaria de ser falado; não citar nada deixaria
 * o botão sem dizer de que campo ele é.
 *
 * ⚠️ Isso depende do `id` que o `Field` publica no `<label>` — `<label htmlFor>`
 * **não rotula botão**. Há teste sentinela em `ui-formulario.test.tsx` afirmando
 * as duas pontas (a at-59 corrigiu justamente o `id` que faltava lá).
 */
interface PropsBotaoSelecao {
  /** O que foi escolhido. Vazio mostra {@link vazio} em cinza. */
  valor: string;
  /** Texto de "nada escolhido ainda" — ex.: "Nenhuma cor selecionada". */
  vazio: string;
  temErro: boolean;
  desabilitado?: boolean;
  title?: string;
  aoAbrir: () => void;
}

const BotaoSelecao: React.FC<PropsBotaoSelecao> = ({
  valor,
  vazio,
  temErro,
  desabilitado,
  title,
  aoAbrir,
}) => {
  const campo = useCampo();
  const idProprio = `${campo?.id ?? 'selecao'}-botao`;

  return (
    <button
      type="button"
      id={idProprio}
      onClick={aoAbrir}
      disabled={desabilitado}
      title={title}
      aria-labelledby={campo?.idRotulo ? `${campo.idRotulo} ${idProprio}` : undefined}
      aria-describedby={campo?.descricao}
      className={[
        molduraCampo(temErro),
        'tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-left',
        desabilitado
          ? 'tw-cursor-not-allowed tw-bg-superficie tw-opacity-70'
          : 'tw-cursor-pointer hover:tw-bg-superficie',
      ].join(' ')}
    >
      {valor ? (
        <span className="tw-font-semibold tw-text-texto-titulo">{valor}</span>
      ) : (
        <span className="tw-text-texto-desabilitado">{vazio}</span>
      )}
      <Icone nome="chevron-baixo" tamanho={16} className="tw-shrink-0 tw-text-texto-desabilitado" />
    </button>
  );
};

export default BotaoSelecao;
